import { useContext, useEffect, useState } from 'react'
import { useGet, useMutate } from 'restful-react'
import { AppContext, AppContextType } from '../AppContext'
import { ChatMessage } from '../types/ChatContracts'
import { UserCommunityProfile, UserContext, UserContextType } from '../UserContext'

export function useCommunityFeature() {
    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { state, dispatch } = useContext(UserContext) as UserContextType

    const {
        mutate: createProfileCall,
        loading: createProfileInProgress,
        error: createProfileError,
    } = useMutate({ verb: 'POST', path: `/v1/users/_me/profile` })

    const {
        mutate: updateProfileCall,
        loading: updateProfileInProgress,
        error: updateProfileError,
    } = useMutate({ verb: 'PATCH', path: `/v1/users/_me/profile` })

    const {
        mutate: deleteProfileCall,
        loading: deleteProfileInProgress,
        error: deleteProfileError,
    } = useMutate({ verb: 'DELETE', path: `/v1/users/_me/profile` })

    const {
        refetch: searchUserProfilesCall,
        data: searchUserProfilesResult,
        error: searchUserProfilesError,
        loading: searchUserProfilesLoading,
    } = useGet({ path: '/v1/search/user-profiles', lazy: true })

    const {
        refetch: searchUsersCall,
        data: searchUsersResult,
        // error: searchUserProfilesError,
        // loading: searchUserProfilesLoading,
    } = useGet({ path: '/v1/search/user-profiles', lazy: true })

    const {
        mutate: sendCollaboratorInvitationCall,
        loading: sendCollaboratorInvitationInProgress,
        error: sendCollaboratorInvitationError,
    } = useMutate({ verb: 'POST', path: ({ userUrn }) => `/v1/users/${userUrn}/collaborator-invitations` })

    const {
        mutate: acceptCollaboratorInvitationCall,
        loading: acceptCollaboratorInvitationInProgress,
        error: acceptCollaboratorInvitationError,
    } = useMutate({ verb: 'POST', path: ({ userUrn }) => `/v1/users/_me/collaborator-invitations/${userUrn}` })

    const {
        mutate: denyCollaboratorInvitationCall,
        loading: denyCollaboratorInvitationInProgress,
        error: denyCollaboratorInvitationError,
    } = useMutate({
        verb: 'DELETE',
        path: ({ userUrn }) => `/v1/users/_me/collaborator-invitations/${userUrn}`,
        resolve: (response) => {
            if (response?.events && Array.isArray(response.events)) {
                dispatch({
                    type: 'set_app_events',
                    value: response.events,
                })
                appDispatch({
                    type: 'root_snackbar_history_append',
                    value: {
                        severity: 'success',
                        message: 'Friend request denied.',
                    },
                })
            }
        },
    })

    const [unfriendCollaboratorComplete, set_unfriendCollaboratorComplete] = useState(0)
    const {
        mutate: unfriendCollaboratorCall,
        loading: unfriendCollaboratorInProgress,
        error: unfriendCollaboratorError,
    } = useMutate({
        verb: 'DELETE',
        path: ({ userUrn }) => `/v1/users/_me/collaborators/${userUrn}`,
        resolve: (data) => {
            set_unfriendCollaboratorComplete((c) => c + 1)
        },
    })

    const {
        refetch: getMyMessageGroupsCall,
        data: getMyMessageGroupsResult,
        loading: getMyMessageGroupsLoading,
        error: getMyMessageGroupsError,
    } = useGet({ path: `/v1/message-groups`, lazy: true })

    const {
        mutate: createNewMessageGroupCall,
        loading: createNewMessageGroupLoading,
        error: createNewMessageGroupError,
    } = useMutate({ verb: 'POST', path: `/v1/message-groups` })

    const [deleteMessageGroupComplete, set_deleteMessageGroupComplete] = useState(0)
    const {
        mutate: deleteMessageGroupCall,
        loading: deleteMessageGroupLoading,
        error: deleteMessageGroupError,
    } = useMutate({
        verb: 'DELETE',
        path: ({ messageGroupUrn }) => `/v1/message-groups/${messageGroupUrn}`,
        resolve: (data) => {
            set_deleteMessageGroupComplete((c) => c + 1)
        },
    })

    const {
        mutate: inviteToSessionCall,
        loading: inviteToSessionLoading,
        error: inviteToSessionError,
    } = useMutate({ verb: 'POST', path: `/v1/requests/session-invitations` })

    // used by the call invitor to cancel the invitation.
    const { mutate: inviteToSessionCancel } = useMutate({ verb: 'DELETE', path: `/v1/requests/session-invitations` })

    // Used by the call event recipient to delete the event.
    const {
        mutate: deleteSessionInviteCall,
        loading: deleteSessionInviteLoading,
        error: deleteSessionInviteError,
    } = useMutate({
        verb: 'DELETE',
        path: ({ uuid }) => `/v1/users/_me/session-invitations/${uuid}`,
        resolve: (response) => {
            if (response?.events && Array.isArray(response.events)) {
                dispatch({
                    type: 'set_app_events',
                    value: response.events,
                })
            }
        },
    })

    const createProfile = async (profile: UserCommunityProfile) => {
        const createProfileResult = await createProfileCall(profile)
        return createProfileResult
    }

    const updateProfile = async (profile: Partial<UserCommunityProfile>) => {
        const updateProfileResult = await updateProfileCall(profile)
        return updateProfileResult
    }

    const deleteProfile = async () => {
        const deleteProfileResult = await deleteProfileCall({})
        return deleteProfileResult
    }

    const searchUserProfiles = ({
        query,
        generalQuery,
        all,
        sort,
        page,
    }: {
        query?: Object
        generalQuery?: string
        all?: boolean
        sort?: Object
        page?: number
    }) => {
        searchUserProfilesCall({
            queryParams: {
                q: query,
                gq: generalQuery,
                all: all ? '1' : undefined,
                page: page ? page : 0,
                sort: JSON.stringify(sort),
                v: 2,
            },
        })
    }

    useEffect(() => {
        if (searchUserProfilesResult) {
            dispatch({
                type: 'update_community_state',
                value: { userProfilesSearchResult: searchUserProfilesResult },
            })
        }
    }, [searchUserProfilesResult])

    const searchUser = ({
        query,
        generalQuery,
        username,
        all,
    }: {
        query?: Object
        generalQuery?: string
        username?: string
        all?: boolean
    }) => {
        searchUsersCall({
            queryParams: {
                q: query,
                gq: generalQuery,
                username: username,
                all: all ? '1' : undefined,
            },
        })
    }

    const sendCollaboratorInvitation = (userUrn: string) => {
        sendCollaboratorInvitationCall(
            {},
            {
                pathParams: { userUrn: userUrn },
            }
        )

        // Update local copy ahead of time.
        const next = state.community.userProfilesSearchResult.profiles.map((u) => {
            if (u.userUrn === userUrn) {
                u.collaboratorStatus = 'pending'
            }
            return u
        })
        dispatch({
            type: 'update_community_state',
            value: {
                userProfilesSearchResult: {
                    ...state.community.userProfilesSearchResult,
                    profiles: next,
                },
            },
        })
    }

    const acceptCollaboratorInvitation = async (userUrn: string) => {
        const response = await acceptCollaboratorInvitationCall(
            {},
            {
                pathParams: { userUrn: userUrn },
            }
        )
        if (response?.events && Array.isArray(response.events)) {
            dispatch({
                type: 'set_app_events',
                value: response.events,
            })
            appDispatch({
                type: 'root_snackbar_history_append',
                value: {
                    severity: 'success',
                    message: 'Friend request accepted!',
                },
            })
        }
    }

    const denyCollaboratorInvitation = (userUrn: string) => {
        denyCollaboratorInvitationCall(
            {},
            {
                pathParams: { userUrn: userUrn },
            }
        )
    }

    const unfriendCollaborator = (userUrn: string) => {
        unfriendCollaboratorCall(
            {},
            {
                pathParams: { userUrn: userUrn },
            }
        )
    }

    const getMyMessageGroups = () => {
        getMyMessageGroupsCall()
    }

    useEffect(() => {
        if (getMyMessageGroupsResult) {
            dispatch({
                type: 'update_community_state',
                value: {
                    messageGroups: getMyMessageGroupsResult,
                },
            })
        }
        if (state.community.selectedMessageGroupId == null) {
        }
    }, [getMyMessageGroupsResult])

    const createNewMessageGroup = async (members: string[], initialMessage?: ChatMessage) => {
        const response = await createNewMessageGroupCall({ members: members, initialMessage: initialMessage })
        const groupId = response._id
        dispatch({
            type: 'update_community_state',
            value: {
                messageGroups: [...state.community.messageGroups, response],
                selectedMessageGroupId: groupId,
            },
        })
    }

    const deleteMessageGroup = (messageGroupUrn: string) => {
        deleteMessageGroupCall(
            {},
            {
                pathParams: { messageGroupUrn: messageGroupUrn },
            }
        )
    }

    const deleteSessionInvite = (uuid: string) => {
        deleteSessionInviteCall(
            {},
            {
                pathParams: { uuid: uuid },
            }
        )
    }

    //const inviteToSessionBasedOnCurrentState = () => {
    //    const currentMessageGroup = state.community.messageGroups.filter(g => g._id === state.community.selectedMessageGroupId)[0] || null
    //    if (state.currentRoom?.roomUrn) {
    //        const userUrns = currentMessageGroup.members
    //            .map(m => m.userUrn)
    //            .filter(userUrn => userUrn !== state.userUrn) // don't include self
    //        inviteToSession({ userUrns: userUrns, roomUrn: state.currentRoom?.roomUrn })
    //    } else {
    //        appDispatch({
    //            type: 'root_snackbar_history_append',
    //            value: {
    //                severity: 'error',
    //                message: 'Please join or create a room before inviting a collaborator.'
    //            }
    //        })
    //    }
    //}

    const [unreadMessagesCount, set_unreadMessagesCount] = useState(0)
    const calculateUnreadMessagesCount = () => {
        let messagesCt = 0
        state.community.messageGroupUnreadMessagesPerGroup.forEach((countOfRoom) => {
            messagesCt += countOfRoom
        })
        return messagesCt
    }
    useEffect(() => {
        set_unreadMessagesCount(calculateUnreadMessagesCount())
    }, [state.community.messageGroupUnreadMessagesPerGroup])

    return {
        createProfile,
        createProfileInProgress,
        createProfileError,

        updateProfile,
        updateProfileInProgress,
        updateProfileError,

        deleteProfile,
        deleteProfileInProgress,
        deleteProfileError,

        searchUserProfiles,
        searchUserProfilesError,
        searchUserProfilesLoading,

        searchUser,
        searchUsersResult,

        sendCollaboratorInvitation,
        acceptCollaboratorInvitation,
        denyCollaboratorInvitation,
        unfriendCollaborator,
        unfriendCollaboratorComplete,

        getMyMessageGroups,
        createNewMessageGroup,
        deleteMessageGroup,
        deleteMessageGroupComplete,

        inviteToSessionCall,
        inviteToSessionLoading,
        inviteToSessionError,
        inviteToSessionCancel,
        deleteSessionInvite,

        unreadMessagesCount,
        calculateUnreadMessagesCount,
    }
}
